import type { NavItemConfig } from "@/types/nav";

/**
 * HUMANDA Portal Navigation Configuration
 *
 * Navigation items for the HUMANDA dashboard sidebar.
 * Uses Devias Kit Pro NavItemConfig pattern.
 * data-testid attributes are included for E2E testing.
 *
 * minTier controls sidebar visibility:
 * - Tier 1: standard, employee, buyer (all users)
 * - Tier 2: team_lead, vendor, broker
 * - Tier 3: manager, seller
 * - Tier 4: executive
 * - Tier 5: admin
 *
 * Items without minTier default to visible for all authenticated users.
 */

/**
 * Extended nav item type with tier-based access control.
 * The minTier field controls visibility in the sidebar.
 */
export interface HumandaNavItemConfig extends NavItemConfig {
	/** Minimum tier level required to see this nav item (1-5). Omit for all users. */
	minTier?: number;
	/**
	 * Restrict visibility to specific userType values.
	 * When present, the nav item is only shown if user.userType is in this list.
	 * Combined with minTier when both are present (both conditions must be met).
	 */
	allowedRoles?: string[];
	/** Nested items with tier support */
	items?: HumandaNavItemConfig[];
}

export const humandaNavItems: HumandaNavItemConfig[] = [
	{
		key: "dashboards",
		title: "Dashboards",
		items: [
			{
				key: "overview",
				title: "Overview",
				href: "/dashboard",
				icon: "house",
				matcher: { type: "equals", href: "/dashboard" },
				"data-testid": "humanda-overview-nav",
			},
			{
				key: "analytics",
				title: "Analytics",
				href: "/dashboard/analytics",
				icon: "chart-pie",
				allowedRoles: ["admin", "executive"],
				"data-testid": "humanda-analytics-nav",
			},
		],
	},
	{
		key: "marketplace",
		title: "M&A Marketplace",
		items: [
			{
				key: "marketplace-search",
				title: "Marketplace",
				href: "/dashboard/marketplace",
				icon: "storefront",
				matcher: { type: "startsWith", href: "/dashboard/marketplace" },
				"data-testid": "humanda-marketplace-nav",
			},
			// REQ-BVDR-FE-7: "My Purchases" nav item removed (legacy data-broker
			// buy-flow retired). The buyer aggregate is now "My BVDRs" (rooms).
			// REQ-BVDR-FE-2: buyer "My BVDRs" / access-request list (append-only)
			{
				key: "rooms",
				title: "My BVDRs",
				href: "/dashboard/rooms",
				icon: "storefront",
				allowedRoles: ["buyer", "executive", "admin"],
				matcher: { type: "startsWith", href: "/dashboard/rooms" },
				"data-testid": "humanda-rooms-nav",
			},
			{
				key: "hchs",
				title: "HCHS Dashboard",
				href: "/dashboard/hchs",
				icon: "file-text",
				matcher: { type: "startsWith", href: "/dashboard/hchs" },
				"data-testid": "humanda-hchs-nav",
			},
			{
				key: "reports",
				title: "Reports",
				href: "/dashboard/reports/history",
				icon: "file-text",
				matcher: { type: "startsWith", href: "/dashboard/reports" },
				"data-testid": "humanda-reports-nav",
			},
			{
				key: "listings",
				title: "Listings",
				href: "/dashboard/listings",
				icon: "storefront",
				minTier: 3,
				matcher: { type: "startsWith", href: "/dashboard/listings" },
				"data-testid": "humanda-listings-nav",
			},
			// REQ-BVDR-FE-3: seller approval console (append-only)
			{
				key: "seller-requests",
				title: "Access Requests",
				href: "/dashboard/seller-requests",
				icon: "users",
				allowedRoles: ["seller", "executive", "admin"],
				matcher: { type: "startsWith", href: "/dashboard/seller-requests" },
				"data-testid": "humanda-seller-requests-nav",
			},
			{
				key: "buyers",
				title: "Buyers",
				href: "/dashboard/buyers",
				icon: "users",
				matcher: { type: "startsWith", href: "/dashboard/buyers" },
				"data-testid": "humanda-buyers-nav",
			},
		],
	},
	{
		key: "broker",
		title: "Broker",
		minTier: 2,
		allowedRoles: ["broker", "executive", "admin"],
		items: [
			{
				key: "broker-dashboard",
				title: "Broker Dashboard",
				href: "/dashboard/broker",
				icon: "chart-pie",
				minTier: 2,
				allowedRoles: ["broker", "executive", "admin"],
				matcher: { type: "startsWith", href: "/dashboard/broker" },
				"data-testid": "humanda-broker-dashboard-nav",
			},
			{
				key: "broker-clients",
				title: "My Clients",
				href: "/dashboard/broker/clients",
				icon: "users",
				minTier: 2,
				allowedRoles: ["broker", "executive", "admin"],
				matcher: { type: "startsWith", href: "/dashboard/broker/clients" },
				"data-testid": "humanda-broker-clients-nav",
			},
			{
				key: "broker-commissions",
				title: "Commissions",
				href: "/dashboard/broker/commissions",
				icon: "coins",
				minTier: 2,
				allowedRoles: ["broker", "executive", "admin"],
				matcher: { type: "startsWith", href: "/dashboard/broker/commissions" },
				"data-testid": "humanda-broker-commissions-nav",
			},
			{
				key: "broker-outreach",
				title: "Outreach",
				href: "/dashboard/broker/outreach",
				icon: "paper-plane",
				minTier: 2,
				allowedRoles: ["broker", "executive", "admin"],
				matcher: { type: "startsWith", href: "/dashboard/broker/outreach" },
				"data-testid": "humanda-broker-outreach-nav",
			},
		],
	},
	{
		key: "billing",
		title: "Billing",
		minTier: 2,
		allowedRoles: ["seller", "broker", "executive", "admin"],
		items: [
			{
				key: "billing-overview",
				title: "Billing & Subscription",
				href: "/dashboard/billing",
				icon: "credit-card",
				minTier: 2,
				allowedRoles: ["seller", "broker", "executive", "admin"],
				matcher: { type: "startsWith", href: "/dashboard/billing" },
				"data-testid": "humanda-billing-nav",
			},
		],
	},
	{
		key: "admin",
		title: "Administration",
		minTier: 5,
		items: [
			{
				key: "access-logs",
				title: "Access Logs",
				href: "/dashboard/admin/access-logs",
				icon: "list",
				minTier: 5,
				matcher: { type: "startsWith", href: "/dashboard/admin/access-logs" },
				"data-testid": "humanda-access-logs-nav",
			},
			{
				key: "forensics",
				title: "Forensic Lookup",
				href: "/dashboard/admin/forensics",
				icon: "magnifying-glass",
				minTier: 5,
				matcher: { type: "startsWith", href: "/dashboard/admin/forensics" },
				"data-testid": "humanda-forensics-nav",
			},
		],
	},
	{
		key: "certifications",
		title: "Certifications",
		items: [
			{
				key: "certifications-catalog",
				title: "Certifications",
				href: "/dashboard/certifications",
				icon: "graduation-cap",
				matcher: { type: "startsWith", href: "/dashboard/certifications" },
				"data-testid": "humanda-certifications-nav",
			},
			{
				key: "admin-certifications",
				title: "Manage Certifications",
				href: "/dashboard/admin/certifications",
				icon: "graduation-cap",
				minTier: 5,
				matcher: { type: "startsWith", href: "/dashboard/admin/certifications" },
				"data-testid": "humanda-admin-certifications-nav",
			},
		],
	},
	{
		key: "support",
		title: "Support",
		items: [
			{
				key: "help-center",
				title: "Help Center",
				href: "/dashboard/help",
				icon: "graduation-cap",
				matcher: { type: "startsWith", href: "/dashboard/help" },
				"data-testid": "nav-help-center",
			},
		],
	},
];
